// src/redux/sagas/imageUploadSaga.ts
import { call, put, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { request } from '../../api/request';
import ENDPOINTS from '../../api/endpoints';
import {
  uploadImageRequest,
  uploadImageSuccess,
  uploadImageFailure,
  UploadImageRequestPayload,
} from '../slices/imageUploadSlice';
import { shouldSkipApiCalls } from '../../utils/appConfig';

// Interface for upload response
interface ImageUploadResponse {
  image_url: string;
}

function* uploadImageSaga(action: PayloadAction<File | UploadImageRequestPayload>): Generator<
  unknown,
  void,
  AxiosResponse<ImageUploadResponse>
> {
  // Skip API calls if in preview mode
  if (shouldSkipApiCalls()) {
    return;
  }

  try {
    // Handle both File and payload with mediaType
    let file: File;
    let mediaType: 'image' | 'video' = 'image';
    
    if (action.payload instanceof File) {
      file = action.payload;
    } else {
      file = action.payload.file;
      mediaType = action.payload.mediaType || 'image';
    }

    // Validate file
    if (!file) {
      yield put(uploadImageFailure('No file provided'));
      return;
    }

    // Validate file type based on mediaType
    const allowedTypes = mediaType === 'video'
      ? ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime']
      : ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    
    if (!allowedTypes.includes(file.type)) {
      const errorMsg = mediaType === 'video'
        ? 'Invalid file type. Please upload a video file (MP4, WebM, OGG, QuickTime).'
        : 'Invalid file type. Please upload an image file (PNG, JPEG, JPG, WebP).';
      yield put(uploadImageFailure(errorMsg));
      return;
    }

    // Validate file size
    const maxSize = mediaType === 'video' 
      ? 50 * 1024 * 1024  // 50MB for videos
      : 10 * 1024 * 1024; // 10MB for images

    if (file.size > maxSize) {
      const errorMsg = mediaType === 'video'
        ? 'Video must be less than 50MB.'
        : 'Image must be less than 10MB.';
      yield put(uploadImageFailure(errorMsg));
      return;
    }


    // Create FormData
    const formData = new FormData();
    formData.append('image', file);

    // Make the upload request
    const response = yield call(request<ImageUploadResponse>, {
      method: 'POST',
      url: '/imageUpload', // This will be appended to IMAGE_UPLOAD_URL
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      base: ENDPOINTS.IMAGE_UPLOAD_URL,
      requiresAuth: false,
      requiresRestaurantId: false,
    });

    // Extract image URL from response
    const uploadedImageUrl = response?.data?.image_url;

    if (!uploadedImageUrl) {
      yield put(uploadImageFailure('Upload successful but no image URL returned'));
      return;
    }

    yield put(uploadImageSuccess(uploadedImageUrl));

  } catch (error: unknown) {
    let errorMessage = 'Failed to upload image';
    
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === 'object' && error !== null) {
      const axiosError = error as any;
      if (axiosError.response?.data?.message) {
        errorMessage = axiosError.response.data.message;
      } else if (axiosError.response?.data?.error) {
        errorMessage = axiosError.response.data.error;
      } else if (axiosError.message) {
        errorMessage = axiosError.message;
      }
    }

    yield put(uploadImageFailure(errorMessage));
  }
}

// Watcher saga
export function* watchImageUploadSaga() {
  yield takeEvery(uploadImageRequest.type, uploadImageSaga);
}
